import React, { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useMasterData } from '../contexts/MasterDataContext';
import { API } from '../utils/apiConfig';
import axios from 'axios';
import { toast } from 'sonner';
import {
  GraduationCap, Plus, Users, UserCheck, IndianRupee, Search,
  Edit2, Trash2, Loader2, Phone, MapPin, Calendar, Clock, Dumbbell, X, Download,
} from 'lucide-react';

const CATEGORIES = ['Kids', 'Adults', 'Senior', 'Ladies'];
const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

const emptyForm = {
  name: '', mobile: '', age: '', skill_level: '', area: '', category: '',
  start_date: '', total_sessions: '', batch_timings: '', coach_name: '',
  venue: '', fees: '', status: 'Active', notes: '',
};

const skillColor = (s) => ({
  Beginner: 'bg-emerald-100 text-emerald-700',
  Intermediate: 'bg-amber-100 text-amber-700',
  Advanced: 'bg-rose-100 text-rose-700',
}[s] || 'bg-slate-100 text-slate-600');

const catColor = (c) => ({
  Kids: 'bg-sky-100 text-sky-700',
  Adults: 'bg-indigo-100 text-indigo-700',
  Senior: 'bg-purple-100 text-purple-700',
  Ladies: 'bg-pink-100 text-pink-700',
}[c] || 'bg-slate-100 text-slate-600');

export default function CoachingPage() {
  const { getVenueNames } = useMasterData();
  const VENUES = getVenueNames();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ enrollments: [], stats: { total_players: 0, active_players: 0, total_fees: 0, by_venue: {} }, coaches: [] });
  const [filters, setFilters] = useState({ venue: 'all', category: 'all', skill_level: 'all', status: 'all', month: new Date().toISOString().slice(0, 7), search: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.venue !== 'all') params.venue = filters.venue;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.skill_level !== 'all') params.skill_level = filters.skill_level;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.month) params.month = filters.month;
      if (filters.search) params.search = filters.search;
      const res = await axios.get(`${API}/coaching`, { params });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load coaching data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...emptyForm, ...row,
      age: row.age ?? '', total_sessions: row.total_sessions ?? '', fees: row.fees ?? '',
    });
    setDialogOpen(true);
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim() || !form.mobile.trim()) {
      toast.error('Player name and mobile are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age === '' ? null : parseInt(form.age, 10),
        total_sessions: form.total_sessions === '' ? 0 : parseInt(form.total_sessions, 10),
        fees: form.fees === '' ? 0 : parseFloat(form.fees),
      };
      if (editing) {
        await axios.put(`${API}/coaching/${editing.id}`, payload);
        toast.success('Enrollment updated');
      } else {
        await axios.post(`${API}/coaching`, payload);
        toast.success('Player enrolled');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save enrollment');
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.venue !== 'all') params.venue = filters.venue;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.skill_level !== 'all') params.skill_level = filters.skill_level;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.month) params.month = filters.month;
      if (filters.search) params.search = filters.search;
      const res = await axios.get(`${API}/coaching/export`, { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `coaching${filters.month ? '_' + filters.month : ''}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (e) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/coaching/${deleting.id}`);
      toast.success('Enrollment removed');
      setDeleting(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const stats = data.stats || {};
  const rows = data.enrollments || [];

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="coaching-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-indigo-100">
              <GraduationCap className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Coaching</h1>
              <p className="text-sm text-slate-500">Players enrolled across coaching programs</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleExport} variant="outline" className="gap-2" disabled={exporting} data-testid="export-coaching-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button onClick={openCreate} className="gap-2" data-testid="add-enrollment-btn">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Enroll Player</span><span className="sm:hidden">Enroll</span>
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="p-4 border-slate-200" data-testid="stat-active">
            <div className="flex items-center gap-2 text-emerald-600"><UserCheck className="w-4 h-4" /><span className="text-xs font-medium">Active Players</span></div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.active_players || 0}</p>
          </Card>
          <Card className="p-4 border-slate-200" data-testid="stat-total">
            <div className="flex items-center gap-2 text-indigo-600"><Users className="w-4 h-4" /><span className="text-xs font-medium">Total Players</span></div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_players || 0}</p>
          </Card>
          <Card className="p-4 border-slate-200" data-testid="stat-fees">
            <div className="flex items-center gap-2 text-amber-600"><IndianRupee className="w-4 h-4" /><span className="text-xs font-medium">Total Fees</span></div>
            <p className="text-2xl font-bold text-slate-900 mt-1">₹{Number(stats.total_fees || 0).toLocaleString('en-IN')}</p>
          </Card>
          <Card className="p-4 border-slate-200" data-testid="stat-venues">
            <div className="flex items-center gap-2 text-sky-600"><MapPin className="w-4 h-4" /><span className="text-xs font-medium">By Venue</span></div>
            <div className="mt-1 space-y-0.5 max-h-16 overflow-y-auto">
              {Object.entries(stats.by_venue || {}).length === 0 ? (
                <p className="text-sm text-slate-400">—</p>
              ) : Object.entries(stats.by_venue || {}).map(([v, n]) => (
                <p key={v} className="text-xs text-slate-600 flex justify-between gap-2"><span className="truncate">{v}</span><span className="font-semibold">{n}</span></p>
              ))}
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-3 border-slate-200">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[160px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search name / mobile / area"
                value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                className="pl-9"
                data-testid="coaching-search"
              />
            </div>
            <Select value={filters.venue} onValueChange={(v) => setFilters((p) => ({ ...p, venue: v }))}>
              <SelectTrigger className="w-36" data-testid="filter-venue"><SelectValue placeholder="Venue" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Venues</SelectItem>
                {VENUES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(v) => setFilters((p) => ({ ...p, category: v }))}>
              <SelectTrigger className="w-32" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.skill_level} onValueChange={(v) => setFilters((p) => ({ ...p, skill_level: v }))}>
              <SelectTrigger className="w-32" data-testid="filter-skill"><SelectValue placeholder="Skill" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Skills</SelectItem>
                {SKILL_LEVELS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.status} onValueChange={(v) => setFilters((p) => ({ ...p, status: v }))}>
              <SelectTrigger className="w-32" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Input
                type="month"
                value={filters.month}
                onChange={(e) => setFilters((p) => ({ ...p, month: e.target.value }))}
                className="w-40"
                title="Filter by enrollment start month"
                data-testid="filter-month"
              />
              {filters.month && (
                <Button variant="ghost" size="sm" className="px-2 text-slate-500" onClick={() => setFilters((p) => ({ ...p, month: '' }))} data-testid="clear-month" title="Clear month filter">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* List */}
        {loading ? (
          <Card className="p-12 text-center border-slate-200"><Loader2 className="w-8 h-8 mx-auto text-indigo-500 animate-spin" /></Card>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <GraduationCap className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-slate-600">No players enrolled yet</p>
            <p className="text-sm text-slate-400">Click "Enroll Player" to add the first one</p>
          </Card>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 sm:hidden">
              {rows.map((r) => (
                <Card key={r.id} className="p-4 border-slate-200" data-testid={`coaching-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{r.name} {r.age ? <span className="text-slate-400 text-sm font-normal">· {r.age}y</span> : null}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{r.mobile}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{r.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {r.category && <span className={`px-2 py-0.5 rounded-full text-xs ${catColor(r.category)}`}>{r.category}</span>}
                    {r.skill_level && <span className={`px-2 py-0.5 rounded-full text-xs ${skillColor(r.skill_level)}`}>{r.skill_level}</span>}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{r.venue || '—'}</span>
                    <span className="flex items-center gap-1"><Dumbbell className="w-3 h-3 text-slate-400" />{r.coach_name || '—'}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-slate-400" />{r.batch_timings || '—'}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-slate-400" />{r.start_date || '—'}</span>
                    <span>Sessions: <b>{r.total_sessions || 0}</b></span>
                    <span>Fees: <b>₹{Number(r.fees || 0).toLocaleString('en-IN')}</b></span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}><Edit2 className="w-4 h-4 mr-1" />Edit</Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleting(r)} data-testid={`delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </Card>
              ))}
            </div>

            {/* Desktop table */}
            <Card className="border-slate-200 overflow-hidden hidden sm:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="coaching-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {['Player', 'Profile', 'Category / Skill', 'Venue / Coach', 'Batch / Start', 'Sessions', 'Fees', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="text-left p-3 font-semibold text-slate-700 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`coaching-row-${r.id}`}>
                        <td className="p-3">
                          <p className="font-medium text-slate-900">{r.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1"><Phone className="w-3 h-3" />{r.mobile}</p>
                        </td>
                        <td className="p-3 text-slate-600 text-xs whitespace-nowrap">
                          {r.age ? `${r.age} yrs` : '—'}{r.area ? ` · ${r.area}` : ''}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            {r.category && <span className={`px-2 py-0.5 rounded-full text-xs w-fit ${catColor(r.category)}`}>{r.category}</span>}
                            {r.skill_level && <span className={`px-2 py-0.5 rounded-full text-xs w-fit ${skillColor(r.skill_level)}`}>{r.skill_level}</span>}
                          </div>
                        </td>
                        <td className="p-3 text-slate-600 text-xs whitespace-nowrap">
                          <p>{r.venue || '—'}</p>
                          <p className="text-slate-400">{r.coach_name || '—'}</p>
                        </td>
                        <td className="p-3 text-slate-600 text-xs whitespace-nowrap">
                          <p>{r.batch_timings || '—'}</p>
                          <p className="text-slate-400">{r.start_date || '—'}</p>
                        </td>
                        <td className="p-3 font-semibold text-slate-700">{r.total_sessions || 0}</td>
                        <td className="p-3 font-semibold text-slate-700">₹{Number(r.fees || 0).toLocaleString('en-IN')}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{r.status}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-${r.id}`}><Edit2 className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleting(r)} data-testid={`delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-indigo-600" />{editing ? 'Edit Enrollment' : 'Enroll Player'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Player Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={(e) => setField('name', e.target.value)} data-testid="form-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile <span className="text-red-500">*</span></Label>
              <Input value={form.mobile} onChange={(e) => setField('mobile', e.target.value)} data-testid="form-mobile" />
            </div>
            <div className="space-y-1.5">
              <Label>Age</Label>
              <Input type="number" min="0" value={form.age} onChange={(e) => setField('age', e.target.value)} data-testid="form-age" />
            </div>
            <div className="space-y-1.5">
              <Label>Area</Label>
              <Input value={form.area} onChange={(e) => setField('area', e.target.value)} data-testid="form-area" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setField('category', v)}>
                <SelectTrigger data-testid="form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Skill Level</Label>
              <Select value={form.skill_level} onValueChange={(v) => setField('skill_level', v)}>
                <SelectTrigger data-testid="form-skill"><SelectValue placeholder="Select skill" /></SelectTrigger>
                <SelectContent>{SKILL_LEVELS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Venue</Label>
              <Select value={form.venue} onValueChange={(v) => setField('venue', v)}>
                <SelectTrigger data-testid="form-venue"><SelectValue placeholder="Select venue" /></SelectTrigger>
                <SelectContent>{VENUES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Coach Name</Label>
              <Input value={form.coach_name} onChange={(e) => setField('coach_name', e.target.value)} data-testid="form-coach" />
            </div>
            <div className="space-y-1.5">
              <Label>Start Date</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setField('start_date', e.target.value)} data-testid="form-start-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Batch Timings</Label>
              <Input placeholder="e.g. 6:00 AM - 7:00 AM" value={form.batch_timings} onChange={(e) => setField('batch_timings', e.target.value)} data-testid="form-batch" />
            </div>
            <div className="space-y-1.5">
              <Label>Total Sessions</Label>
              <Input type="number" min="0" value={form.total_sessions} onChange={(e) => setField('total_sessions', e.target.value)} data-testid="form-sessions" />
            </div>
            <div className="space-y-1.5">
              <Label>Fees (₹)</Label>
              <Input type="number" min="0" value={form.fees} onChange={(e) => setField('fees', e.target.value)} data-testid="form-fees" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setField('status', v)}>
                <SelectTrigger data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-enrollment-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (editing ? 'Update' : 'Enroll')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Remove enrollment?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will permanently remove <b>{deleting?.name}</b> from coaching records.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
